import { useRef } from 'react';
import PricingCTA from './PricingCTA';

const Veritas = () => {
  const heroVideoRef = useRef(null);

  return (
    <div className="absolute inset-0 w-full bg-black overflow-y-auto">
      {/* Hero Section */}
      <div className="h-screen relative">
        <div className="absolute inset-0 bg-black/60 z-10" />
        <video
          ref={heroVideoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => console.error('Video error:', e)}
          onCanPlay={(e) => e.target.play().catch(err => console.error('Autoplay failed:', err))}
        >
          <source src="https://storage.googleapis.com/background-for-orchestra/AdobeStock_577279517-2.mp4" type="video/mp4" />
        </video>

        <div className="relative z-20 container mx-auto px-4 pt-32">
          <h1 className="text-5xl md:text-7xl text-white font-light tracking-wider mb-6 drop-shadow-lg">
            Veritas
          </h1>
          <p className="text-xl md:text-2xl text-white/80 font-light tracking-wide max-w-2xl">
            Video evidence to <span className="font-medium text-white">automate car crash claims</span> in San Francisco.
          </p>
        </div>

        {/* Scroll indicator positioned at bottom of hero */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
          <div className="animate-bounce flex flex-col items-center">
            <div className="bg-black/40 p-3 rounded-full border border-white/20 backdrop-blur-sm mb-2">
              <svg className="w-6 h-6 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
            <span className="text-xs text-white/70 tracking-wider">SCROLL</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative bg-black py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Value Proposition */}
            <div className="mb-24">
              <h3 className="text-3xl md:text-4xl text-white font-light tracking-wider mb-6 max-w-2xl mx-auto relative pb-3">
                The Truth, Whole Truth, and Nothing But the Truth for Auto Insurance
              </h3>
              <div className="w-16 h-1 bg-blue-600 rounded-full mb-6 mx-auto"></div>
              <p className="text-lg text-white/80 font-light tracking-wide mb-12 max-w-3xl">
                <span className="block mb-4">Most car crash claims still rely on hearsay.</span>
                <span className="block mb-4"><i>"He hit me first." "No, she ran into me."</i></span>
                <span className="block mb-4">Veritas replaces stories with truth — clear video of what actually happened.</span>
                <span className="block mb-4"><strong>Videos don't lie.</strong></span>
              </p>

            </div>


            {/* Insurance Fraud Statistics Section */}
            <div className="mb-24">
              <h2 className="text-3xl md:text-4xl text-white font-light tracking-wider mb-6">
                Insurance Fraud Impacts Everyone
              </h2>
              <div className="grid md:grid-cols-3 gap-8 mb-4">
                <div className="bg-black/50 rounded-lg p-8 relative overflow-hidden border border-white/10 backdrop-blur-sm">
                  <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-red-700/20 to-transparent rounded-br-full"></div>
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-red-700/10 to-transparent rounded-tl-full"></div>
                  <h3 className="text-4xl text-white font-extralight tracking-wide mb-3">$20 Billion</h3>
                  <div className="w-10 h-px bg-red-700/70 mb-4"></div>
                  <p className="text-white/80 font-light">Annual cost of auto insurance fraud in the U.S.</p>
                </div>

                <div className="bg-black/50 rounded-lg p-8 relative overflow-hidden border border-white/10 backdrop-blur-sm">
                  <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-blue-600/20 to-transparent rounded-br-full"></div>
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-blue-600/10 to-transparent rounded-tl-full"></div>
                  <h3 className="text-4xl text-white font-extralight tracking-wide mb-3">~10%</h3>
                  <div className="w-10 h-px bg-blue-600/70 mb-4"></div>
                  <p className="text-white/80 font-light">Estimated solve rate for hit-and-run crashes in California</p>
                </div>

                <div className="bg-black/50 rounded-lg p-8 relative overflow-hidden border border-white/10 backdrop-blur-sm">
                  <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-amber-500/20 to-transparent rounded-br-full"></div>
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-amber-500/10 to-transparent rounded-tl-full"></div>
                  <h3 className="text-4xl text-white font-extralight tracking-wide mb-3">$100-$300</h3>
                  <div className="w-10 h-px bg-amber-500/70 mb-4"></div>
                  <p className="text-white/80 font-light">Additional annual premium cost for every driver due to insurance fraud</p>
                </div>
              </div>

              <div className="w-full flex justify-end text-white/60 text-sm italic mb-8">
                <span>Source: <a href="https://www.insurance.ca.gov/0500-about-us/02-department/035-eb/fraud-history.cfm" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-300 underline">California Department of Insurance</a></span>
              </div>

              <div className="bg-white/5 rounded-lg p-8">
                <p className="text-white/80 font-light text-lg mb-6">
                  There's still no system to verify what actually happened at the moment of a crash — or who was at fault.
                </p>

                <div className="my-8 border-l-2 border-blue-600/40 pl-4">
                  <p className="text-white font-medium mb-3">
                    We're building the infrastructure to power that system:
                  </p>
                  <p className="text-white/80 font-light">
                    A citywide camera network that captures every car crash and delivers verified footage to insurance providers.
                  </p>
                </div>

                <div className="mt-10 text-center">
                  <p className="text-xl text-white font-light">
                    Veritas brings video evidence to every crash — <span className="text-blue-400 font-normal">finally</span>.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center mb-0">
              <h3 className="text-2xl md:text-3xl text-white font-light tracking-wider mb-1">
                Need accident footage for your insurance claim?
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Add PricingCTA before closing divs */}
      <PricingCTA className="-mt-12 mb-24" />
    </div >
  );
};

export default Veritas; 